// NOTE: This file is stable and usually should not be modified.
// It is important that all functionality in this file is preserved, and should only be modified if explicitly requested.

import React, { useState, useEffect, useRef } from 'react';
import { Download, Upload, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from '@/hooks/useToast';
import { useLoginActions } from '@/hooks/useLoginActions';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { generateSecretKey, getPublicKey, nip19 } from 'nostr-tools';

interface SignupDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const SignupDialog: React.FC<SignupDialogProps> = ({ isOpen, onClose }) => {
  const [step, setStep] = useState<'generate' | 'download' | 'profile'>('generate');
  const [nsec, setNsec] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    about: '',
    picture: ''
  });
  const login = useLoginActions();
  const { mutateAsync: publishEvent, isPending: isPublishing } = useNostrPublish();
  const { mutateAsync: uploadFile, isPending: isUploading } = useUploadFile();
  const avatarFileInputRef = useRef<HTMLInputElement>(null);

  // Generate a proper nsec key using nostr-tools
  const generateKey = () => {
    const sk = generateSecretKey();
    setNsec(nip19.nsecEncode(sk));
    setStep('download');
  };

  const downloadKey = () => {
    try {
      // Create a blob with the key text
      const blob = new Blob([nsec], { type: 'text/plain; charset=utf-8' });
      const url = globalThis.URL.createObjectURL(blob);

      const decoded = nip19.decode(nsec);
      if (decoded.type !== 'nsec') {
        throw new Error('Invalid nsec key');
      }

      const pubkey = getPublicKey(decoded.data);
      const npub = nip19.npubEncode(pubkey);
      const filename = `nostr-${location.hostname.replaceAll(/\./g, '-')}-${npub.slice(5, 9)}.nsec.txt`;

      // Create a temporary link element and trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();

      // Clean up immediately
      globalThis.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Continue to profile step
      login.nsec(nsec);
      setStep('profile');
    } catch {
      toast({
        title: 'Download failed',
        description: 'Could not download the key file. Please copy it manually.',
        variant: 'destructive',
      });
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset file input
    e.target.value = '';

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select an image file for your avatar.',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Avatar image must be smaller than 5MB.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const tags = await uploadFile(file);
      // Get the URL from the first tag
      const url = tags[0]?.[1];
      if (url) {
        setProfileData(prev => ({ ...prev, picture: url }));
      }
    } catch {
      toast({
        title: 'Upload failed',
        description: 'Failed to upload avatar. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const finishSignup = async (skipProfile = false) => {
    try {
      // Publish profile if user provided information
      if (!skipProfile && (profileData.name || profileData.about || profileData.picture)) {
        const metadata: Record<string, string> = {};
        if (profileData.name) metadata.name = profileData.name;
        if (profileData.about) metadata.about = profileData.about;
        if (profileData.picture) metadata.picture = profileData.picture;

        await publishEvent({
          kind: 0,
          content: JSON.stringify(metadata),
        });
      }
    } catch {
      toast({
        title: 'Profile Setup Failed',
        description: 'Your account was created but profile setup failed. You can update it later.',
        variant: 'destructive',
      });
    } finally {
      onClose();
    }
  };

  const getTitle = () => {
    if (step === 'generate') return 'Sign up';
    if (step === 'download') return 'Secret Key';
    if (step === 'profile') return 'Create Your Profile';
  };

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setStep('generate');
      setNsec('');
      setShowKey(false);
      setProfileData({ name: '', about: '', picture: '' });
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-sm max-h-[90dvh] p-0 gap-0 overflow-hidden rounded-none overflow-y-auto bg-cream border-0 shadow-[0_8px_40px_rgba(45,27,78,0.25)]">
        {/* Decorative tape at top */}
        <div className="relative flex justify-center pt-2">
          <div
            className="w-[100px] h-[20px] tape-rainbow"
            style={{ transform: 'rotate(1.5deg)' }}
          />
        </div>

        <DialogHeader className="px-6 pt-3 pb-0">
          <DialogTitle className="font-hand text-2xl font-bold text-pol-text text-center">
            {getTitle()}
          </DialogTitle>
        </DialogHeader>

        <div className='px-6 pb-6 pt-4 space-y-4 overflow-y-auto flex-1'>
          {/* Generate Step */}
          {step === 'generate' && (
            <div className='text-center space-y-6'>
              {/* Key icon as a mini polaroid */}
              <div className="flex justify-center">
                <div className="bg-polaroid-white p-3 pb-5 shadow-[0_2px_10px_rgba(45,27,78,0.12)]" style={{ transform: 'rotate(2deg)' }}>
                  <div className="w-28 h-24 bg-cream-dark flex items-center justify-center">
                    <span className="text-6xl">🔑</span>
                  </div>
                </div>
              </div>

              <button
                className="relative w-full bg-polaroid-white py-3.5 px-6 shadow-[0_2px_8px_rgba(45,27,78,0.1)] transition-all active:scale-[0.97] hover:-translate-y-0.5 hover:shadow-[0_4px_14px_rgba(45,27,78,0.14)]"
                onClick={generateKey}
                style={{ transform: 'rotate(-0.5deg)' }}
              >
                {/* Mini tape strip */}
                <div
                  className="absolute top-0 left-1/2 w-[50px] h-[12px] tape-rainbow"
                  style={{ transform: 'translateX(-50%) translateY(-6px) rotate(1deg)' }}
                />
                <span className="font-hand text-lg font-bold text-pol-text">Generate key</span>
              </button>
            </div>
          )}

          {/* Download Step */}
          {step === 'download' && (
            <div className='space-y-4'>
              {/* Small key icon polaroid */}
              <div className="flex justify-center">
                <div className="bg-polaroid-white p-2 pb-3 shadow-[0_2px_6px_rgba(45,27,78,0.1)]" style={{ transform: 'rotate(-1deg)' }}>
                  <div className="w-14 h-12 bg-cream-dark flex items-center justify-center">
                    <span className="text-3xl">🔑</span>
                  </div>
                </div>
              </div>

              <div className="relative">
                <Input
                  type={showKey ? "text" : "password"}
                  value={nsec}
                  readOnly
                  className="pr-10 font-mono rounded-none bg-polaroid-white border-cream-dark text-sm"
                />
                <button
                  type="button"
                  className="absolute right-0 top-0 h-full px-3 text-pol-text-secondary hover:text-pol-text transition-colors"
                  onClick={() => setShowKey(!showKey)}
                >
                  {showKey ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>

              <button
                className="relative w-full bg-polaroid-white py-3.5 px-6 shadow-[0_2px_8px_rgba(45,27,78,0.1)] transition-all active:scale-[0.97] hover:-translate-y-0.5 hover:shadow-[0_4px_14px_rgba(45,27,78,0.14)] flex items-center justify-center gap-2"
                onClick={downloadKey}
              >
                <Download className="w-4 h-4 text-pol-text" />
                <span className="font-hand text-lg font-bold text-pol-text">Download key</span>
              </button>

              {/* Warning as a sticky note */}
              <div className='mx-auto max-w-sm'>
                <div
                  className='p-3 bg-[#f1bf60] shadow-[1px_2px_4px_rgba(0,0,0,0.1)]'
                  style={{ transform: 'rotate(0.5deg)' }}
                >
                  <div className='flex items-center gap-2 mb-1'>
                    <span className='font-hand text-sm font-bold text-[#2D1B4E]'>
                      ⚠️ Important Warning
                    </span>
                  </div>
                  <p className='font-hand text-[13px] text-[#2D1B4E] leading-snug'>
                    This key is your primary and only means of accessing your account. Store it safely and securely. Please download your key to continue.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Profile Step */}
          {step === 'profile' && (
            <div className='text-center space-y-4'>
              {/* Publishing status indicator */}
              {isPublishing && (
                <div
                  className='p-4 bg-polaroid-white shadow-[0_2px_6px_rgba(45,27,78,0.1)]'
                >
                  <div className='flex items-center justify-center gap-3'>
                    <div className='w-5 h-5 border-2 border-pol-pink border-t-transparent rounded-full animate-spin' />
                    <span className='font-hand text-sm text-pol-text'>
                      Publishing your profile...
                    </span>
                  </div>
                </div>
              )}

              {/* Profile form — notebook paper style */}
              <div
                className={`space-y-4 text-left ${isPublishing ? 'opacity-50 pointer-events-none' : ''}`}
                style={{
                  backgroundColor: '#FFFFFF',
                  backgroundImage: 'repeating-linear-gradient(to bottom, transparent, transparent 27px, rgba(180,200,220,0.25) 27px, rgba(180,200,220,0.25) 28px)',
                  backgroundPosition: '0 8px',
                  padding: '16px',
                  boxShadow: '1px 2px 4px rgba(0,0,0,0.08)',
                }}
              >
                <div className='space-y-2'>
                  <label htmlFor='profile-name' className='font-hand text-sm font-bold text-pol-text'>
                    Display Name
                  </label>
                  <Input
                    id='profile-name'
                    value={profileData.name}
                    onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder='Your name'
                    disabled={isPublishing}
                    className="rounded-none bg-transparent border-cream-dark/50 font-hand text-base"
                  />
                </div>

                <div className='space-y-2'>
                  <label htmlFor='profile-about' className='font-hand text-sm font-bold text-pol-text'>
                    Bio
                  </label>
                  <Textarea
                    id='profile-about'
                    value={profileData.about}
                    onChange={(e) => setProfileData(prev => ({ ...prev, about: e.target.value }))}
                    placeholder='Tell others about yourself...'
                    className='resize-none rounded-none bg-transparent border-cream-dark/50 font-hand text-base'
                    rows={3}
                    disabled={isPublishing}
                  />
                </div>

                <div className='space-y-2'>
                  <label htmlFor='profile-picture' className='font-hand text-sm font-bold text-pol-text'>
                    Avatar
                  </label>
                  <div className='flex gap-2'>
                    <Input
                      id='profile-picture'
                      value={profileData.picture}
                      onChange={(e) => setProfileData(prev => ({ ...prev, picture: e.target.value }))}
                      placeholder='https://example.com/your-avatar.jpg'
                      className='flex-1 rounded-none bg-transparent border-cream-dark/50 font-mono text-xs'
                      disabled={isPublishing}
                    />
                    <input
                      type='file'
                      accept='image/*'
                      className='hidden'
                      ref={avatarFileInputRef}
                      onChange={handleAvatarUpload}
                    />
                    <button
                      type='button'
                      onClick={() => avatarFileInputRef.current?.click()}
                      disabled={isUploading || isPublishing}
                      title='Upload avatar image'
                      className="bg-polaroid-white w-10 h-10 flex items-center justify-center shadow-[0_2px_6px_rgba(45,27,78,0.1)] transition-all active:scale-[0.95] text-pol-text disabled:opacity-40"
                    >
                      {isUploading ? (
                        <div className='w-4 h-4 border-2 border-pol-pink border-t-transparent rounded-full animate-spin' />
                      ) : (
                        <Upload className='w-4 h-4' />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className='space-y-3'>
                <button
                  className="relative w-full bg-polaroid-white py-3.5 px-6 shadow-[0_2px_8px_rgba(45,27,78,0.1)] transition-all active:scale-[0.97] hover:-translate-y-0.5 hover:shadow-[0_4px_14px_rgba(45,27,78,0.14)] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  onClick={() => finishSignup(false)}
                  disabled={isPublishing || isUploading}
                >
                  {isPublishing ? (
                    <>
                      <div className='w-4 h-4 border-2 border-pol-pink border-t-transparent rounded-full animate-spin' />
                      <span className="font-hand text-lg font-bold text-pol-text">Creating Profile...</span>
                    </>
                  ) : (
                    <span className="font-hand text-lg font-bold text-pol-text">Create profile</span>
                  )}
                </button>

                <button
                  className="w-full py-2.5 font-hand text-sm text-pol-text-secondary hover:text-pol-text transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  onClick={() => finishSignup(true)}
                  disabled={isPublishing || isUploading}
                >
                  {isPublishing ? (
                    'Setting up account...'
                  ) : (
                    'Skip for now'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SignupDialog;
